import { findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { contoursToPathData, pathDataToContours } from "@/document/operations/pathGeometry";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import {
  applyAffinePoint,
  applyAffineVector,
  affineRotation,
  IDENTITY_AFFINE,
  invertAffine,
  multiplyAffine,
  type Affine2D,
} from "@/lib/math/affine";
import type { Rect } from "@/lib/math/rect";
import { unionRects } from "@/lib/math/rect";
import {
  boundsFromPoints,
  inverseRotatePoint,
  normalizeDegrees,
  rectCenter,
  rectCorners,
  rotatePoint,
  rotationOriginForBounds,
  type Point,
} from "@/lib/math/rotation";

export interface ScenePreview {
  nodeId: string;
  bounds: Rect;
  rotation?: number;
}

export class SceneTransformCache {
  private readonly previewsById: Map<string, ScenePreview>;
  private readonly nodeById = new Map<string, SceneNode>();
  private readonly parentIdById = new Map<string, string | undefined>();
  private readonly ancestorIdsById = new Map<string, string[]>();
  private readonly lockedById = new Map<string, boolean>();
  private readonly booleanAncestorIdById = new Map<string, string | undefined>();
  private readonly localToWorldById = new Map<string, Affine2D>();
  private readonly worldToLocalById = new Map<string, Affine2D>();
  private readonly cornersById = new Map<string, Point[]>();
  private readonly interactionBoundsById = new Map<string, Rect>();
  private readonly subtreeInteractionBoundsById = new Map<string, Rect>();

  constructor(document: CurvaDocument, previews: ScenePreview[] = []) {
    this.previewsById = previewById(previews);
    for (const page of document.pages) {
      this.indexChildren(page.children, undefined, [], false, undefined);
      this.finalizeSubtreeBounds(page.children);
    }
  }

  getNode(nodeId: string): SceneNode | undefined {
    return this.nodeById.get(nodeId);
  }

  parentId(nodeId: string): string | undefined {
    return this.parentIdById.get(nodeId);
  }

  previewFor(nodeId: string): ScenePreview | undefined {
    return this.previewsById.get(nodeId);
  }

  isLockedForEditing(nodeId: string): boolean {
    return this.lockedById.get(nodeId) ?? false;
  }

  isDescendantOf(nodeId: string, ancestorId: string): boolean {
    return this.ancestorIdsById.get(nodeId)?.includes(ancestorId) ?? false;
  }

  /** Ancestor node ids ordered from the outermost ancestor down to the parent. */
  ancestorIds(nodeId: string): string[] {
    return this.ancestorIdsById.get(nodeId) ?? [];
  }

  nearestBooleanAncestor(nodeId: string): SceneNode | undefined {
    const ancestorId = this.booleanAncestorIdById.get(nodeId);
    return ancestorId ? this.nodeById.get(ancestorId) : undefined;
  }

  localBounds(node: SceneNode): Rect {
    return nodeSelectionLocalBounds(node, this.previewFor(node.id));
  }

  localToWorldMatrix(nodeId: string): Affine2D {
    const cached = this.localToWorldById.get(nodeId);
    if (cached) {
      return cached;
    }

    const node = this.nodeById.get(nodeId);
    if (!node) {
      return IDENTITY_AFFINE;
    }

    const parentId = this.parentIdById.get(nodeId);
    const parentMatrix = parentId ? this.localToWorldMatrix(parentId) : IDENTITY_AFFINE;
    const matrix = multiplyAffine(parentMatrix, nodeRotationMatrix(node, this.previewFor(nodeId)));
    this.localToWorldById.set(nodeId, matrix);
    return matrix;
  }

  worldToLocalMatrix(nodeId: string): Affine2D {
    const cached = this.worldToLocalById.get(nodeId);
    if (cached) {
      return cached;
    }

    const matrix = invertAffine(this.localToWorldMatrix(nodeId)) ?? IDENTITY_AFFINE;
    this.worldToLocalById.set(nodeId, matrix);
    return matrix;
  }

  pointToWorld(nodeId: string, point: Point): Point {
    return applyAffinePoint(this.localToWorldMatrix(nodeId), point);
  }

  pointToLocal(nodeId: string, point: Point): Point {
    return applyAffinePoint(this.worldToLocalMatrix(nodeId), point);
  }

  selectionCorners(nodeId: string): Point[] | undefined {
    const cached = this.cornersById.get(nodeId);
    if (cached) {
      return cached;
    }

    const node = this.nodeById.get(nodeId);
    if (!node) {
      return undefined;
    }

    const bounds = this.localBounds(node);
    const matrix = this.localToWorldMatrix(nodeId);
    const corners = rectCorners(bounds).map(corner => applyAffinePoint(matrix, corner));
    this.cornersById.set(nodeId, corners);
    return corners;
  }

  interactionBounds(node: SceneNode): Rect {
    const cached = this.interactionBoundsById.get(node.id);
    if (cached) {
      return cached;
    }

    const corners = this.selectionCorners(node.id);
    const bounds = corners && corners.length > 0 ? boundsFromPoints(corners) : node.bounds;
    this.interactionBoundsById.set(node.id, bounds);
    return bounds;
  }

  subtreeInteractionBounds(nodeId: string): Rect | undefined {
    return this.subtreeInteractionBoundsById.get(nodeId);
  }

  hasCompositeSelectionRotation(nodeId: string): boolean {
    const node = this.nodeById.get(nodeId);
    if (!node) {
      return false;
    }

    if (nodeSelfRotation(node, this.previewFor(nodeId)) !== 0) {
      return true;
    }

    let parentId = this.parentIdById.get(nodeId);
    while (parentId) {
      const parent = this.nodeById.get(parentId);
      if (parent && isRotatingContainer(parent) && nodeSelfRotation(parent, this.previewFor(parent.id)) !== 0) {
        return true;
      }
      parentId = this.parentIdById.get(parentId);
    }

    return false;
  }

  private indexChildren(
    children: SceneNode[],
    parentId: string | undefined,
    ancestors: string[],
    lockedAncestor: boolean,
    booleanAncestorId: string | undefined,
  ): void {
    for (const node of children) {
      this.nodeById.set(node.id, node);
      this.parentIdById.set(node.id, parentId);
      this.ancestorIdsById.set(node.id, ancestors);
      this.lockedById.set(node.id, lockedAncestor || node.locked);
      this.booleanAncestorIdById.set(node.id, booleanAncestorId);
      const nestedChildren = getSceneNodeChildren(node);
      if (nestedChildren) {
        this.indexChildren(
          nestedChildren,
          node.id,
          [...ancestors, node.id],
          lockedAncestor || node.locked,
          node.type === "boolean" ? node.id : booleanAncestorId,
        );
      }
    }
  }

  private finalizeSubtreeBounds(children: SceneNode[]): void {
    for (const node of children) {
      const nestedChildren = getSceneNodeChildren(node);
      if (nestedChildren) {
        this.finalizeSubtreeBounds(nestedChildren);
      }

      const parts = [this.interactionBounds(node)];
      if (nestedChildren) {
        for (const child of nestedChildren) {
          const childSubtree = this.subtreeInteractionBoundsById.get(child.id);
          if (childSubtree) {
            parts.push(childSubtree);
          }
        }
      }

      const subtree = unionRects(parts) ?? parts[0]!;
      this.subtreeInteractionBoundsById.set(node.id, subtree);
    }
  }
}

const sceneTransformCachesWithoutPreviews = new WeakMap<CurvaDocument, SceneTransformCache>();
const sceneTransformCachesByPreviews = new WeakMap<CurvaDocument, WeakMap<ScenePreview[], SceneTransformCache>>();

export function createSceneTransformCache(document: CurvaDocument, previews: ScenePreview[] = []): SceneTransformCache {
  return new SceneTransformCache(document, previews);
}

/**
 * Returns the retained interaction transform state for an immutable document /
 * preview pair. Pointer hover queries use this instead of rebuilding the full
 * scene hierarchy and subtree bounds for every pointermove event.
 */
export function getCachedSceneTransformCache(
  document: CurvaDocument,
  previews: ScenePreview[] = [],
): SceneTransformCache {
  if (previews.length === 0) {
    const cached = sceneTransformCachesWithoutPreviews.get(document);
    if (cached) {
      return cached;
    }

    const created = createSceneTransformCache(document);
    sceneTransformCachesWithoutPreviews.set(document, created);
    return created;
  }

  let cachesByPreviews = sceneTransformCachesByPreviews.get(document);
  if (!cachesByPreviews) {
    cachesByPreviews = new WeakMap<ScenePreview[], SceneTransformCache>();
    sceneTransformCachesByPreviews.set(document, cachesByPreviews);
  }

  const cached = cachesByPreviews.get(previews);
  if (cached) {
    return cached;
  }

  const created = createSceneTransformCache(document, previews);
  cachesByPreviews.set(previews, created);
  return created;
}

function isRotatingContainer(node: SceneNode): boolean {
  return node.type === "frame"
    || node.type === "group"
    || node.type === "component"
    || node.type === "componentVariant"
    || node.type === "instance";
}

function previewById(previews: ScenePreview[]): Map<string, ScenePreview> {
  return new Map(previews.filter(preview => preview.nodeId !== "__marquee__").map(preview => [preview.nodeId, preview] as const));
}

export function nodeAncestorChain(document: CurvaDocument, nodeId: string): SceneNode[] {
  for (const page of document.pages) {
    const chain = findNodeAncestorChain(page.children, nodeId, []);
    if (chain) {
      return chain;
    }
  }
  return [];
}

function findNodeAncestorChain(children: SceneNode[], nodeId: string, ancestors: SceneNode[]): SceneNode[] | undefined {
  for (const node of children) {
    if (node.id === nodeId) {
      return ancestors;
    }

    const nestedChildren = getSceneNodeChildren(node);
    if (!nestedChildren) {
      continue;
    }

    const nested = findNodeAncestorChain(nestedChildren, nodeId, [...ancestors, node]);
    if (nested) {
      return nested;
    }
  }

  return undefined;
}

function rotatingAncestorsInnermostFirst(document: CurvaDocument, nodeId: string): SceneNode[] {
  return nodeAncestorChain(document, nodeId).filter(isRotatingContainer).reverse();
}

export function nodeRotationOrigin(node: SceneNode, preview?: ScenePreview): Point {
  const bounds = preview?.bounds ?? node.bounds;
  if (preview?.rotation !== undefined && node.transform.rotation === 0) {
    return rectCenter(bounds);
  }

  return rotationOriginForBounds(bounds, node.bounds, node.transform);
}

export function nodeSelectionLocalBounds(node: SceneNode, preview?: ScenePreview): Rect {
  return preview?.bounds ?? node.bounds;
}

export function nodeSelfRotation(node: SceneNode, preview?: ScenePreview): number {
  return preview?.rotation ?? node.transform.rotation ?? 0;
}

function nodeRotationMatrix(node: SceneNode, preview?: ScenePreview): Affine2D {
  const rotation = nodeSelfRotation(node, preview);
  if (rotation === 0) {
    return IDENTITY_AFFINE;
  }

  return affineRotation(rotation, nodeRotationOrigin(node, preview));
}

export function nodeSelectionLocalToWorldMatrix(
  document: CurvaDocument,
  nodeId: string,
  previews: ScenePreview[] = [],
): Affine2D {
  return nodeDisplayWorldTransform(document, nodeId, previews);
}

export function nodeDisplayWorldTransform(
  document: CurvaDocument,
  nodeId: string,
  previews: ScenePreview[] = [],
): Affine2D {
  const node = findSceneNode(document, nodeId);
  if (!node) {
    return IDENTITY_AFFINE;
  }

  const previewsById = previewById(previews);
  let matrix = IDENTITY_AFFINE;
  for (const ancestor of nodeAncestorChain(document, nodeId)) {
    matrix = multiplyAffine(matrix, nodeRotationMatrix(ancestor, previewsById.get(ancestor.id)));
  }
  return multiplyAffine(matrix, nodeRotationMatrix(node, previewsById.get(nodeId)));
}

export function nodeSelectionWorldToLocalMatrix(
  document: CurvaDocument,
  nodeId: string,
  previews: ScenePreview[] = [],
): Affine2D {
  return invertAffine(nodeSelectionLocalToWorldMatrix(document, nodeId, previews)) ?? IDENTITY_AFFINE;
}

function nodeAncestorWorldToLocalMatrix(
  document: CurvaDocument,
  nodeId: string,
  previews: ScenePreview[] = [],
): Affine2D {
  const previewsById = previewById(previews);
  let matrix = IDENTITY_AFFINE;
  for (const ancestor of rotatingAncestorsInnermostFirst(document, nodeId)) {
    matrix = multiplyAffine(nodeRotationMatrix(ancestor, previewsById.get(ancestor.id)), matrix);
  }
  return invertAffine(matrix) ?? IDENTITY_AFFINE;
}

export function nodeHasCompositeSelectionRotation(document: CurvaDocument, nodeId: string, previews: ScenePreview[] = []): boolean {
  const node = findSceneNode(document, nodeId);
  if (!node) {
    return false;
  }

  const previewsById = previewById(previews);
  if (nodeSelfRotation(node, previewsById.get(nodeId)) !== 0) {
    return true;
  }

  return rotatingAncestorsInnermostFirst(document, nodeId).some(ancestor => {
    const ancestorPreview = previewsById.get(ancestor.id);
    return nodeSelfRotation(ancestor, ancestorPreview) !== 0;
  });
}

export function nodeSelectionPointToWorld(
  document: CurvaDocument,
  nodeId: string,
  point: Point,
  previews: ScenePreview[] = [],
): Point {
  return applyAffinePoint(nodeSelectionLocalToWorldMatrix(document, nodeId, previews), point);
}

export function nodeSelectionPointToLocal(
  document: CurvaDocument,
  nodeId: string,
  point: Point,
  previews: ScenePreview[] = [],
): Point {
  return applyAffinePoint(nodeSelectionWorldToLocalMatrix(document, nodeId, previews), point);
}

export function nodeSelectionDragDeltaToLocal(
  document: CurvaDocument,
  nodeId: string,
  worldDelta: Point,
  previews: ScenePreview[] = [],
): Point {
  return applyAffineVector(nodeAncestorWorldToLocalMatrix(document, nodeId, previews), worldDelta);
}

export function nodeSelectionWorldDeltaToLocal(
  document: CurvaDocument,
  nodeId: string,
  worldDelta: Point,
  previews: ScenePreview[] = [],
): Point {
  return applyAffineVector(nodeSelectionWorldToLocalMatrix(document, nodeId, previews), worldDelta);
}

export function nodeSelectionCorners(
  document: CurvaDocument,
  nodeId: string,
  previews: ScenePreview[] = [],
): Point[] | undefined {
  const node = findSceneNode(document, nodeId);
  if (!node) {
    return undefined;
  }

  const previewsById = previewById(previews);
  const bounds = nodeSelectionLocalBounds(node, previewsById.get(nodeId));
  const matrix = nodeSelectionLocalToWorldMatrix(document, nodeId, previews);
  return rectCorners(bounds).map(corner => applyAffinePoint(matrix, corner));
}

export function nodeSelectionInteractionBounds(
  document: CurvaDocument,
  node: SceneNode,
  previews: ScenePreview[] = [],
): Rect {
  const corners = nodeSelectionCorners(document, node.id, previews);
  if (!corners || corners.length === 0) {
    return node.bounds;
  }

  return boundsFromPoints(corners);
}

export function nodeEffectiveWorldRotation(
  document: CurvaDocument,
  nodeId: string,
  previews: ScenePreview[] = [],
): number {
  const node = findSceneNode(document, nodeId);
  if (!node) {
    return 0;
  }

  const previewsById = previewById(previews);
  let rotation = nodeSelfRotation(node, previewsById.get(nodeId));
  for (const ancestor of rotatingAncestorsInnermostFirst(document, nodeId)) {
    rotation += nodeSelfRotation(ancestor, previewsById.get(ancestor.id));
  }
  return normalizeDegrees(rotation);
}

export function rotatingContainersForChildOf(document: CurvaDocument, parentId: string): SceneNode[] {
  const parent = findSceneNode(document, parentId);
  const containers: SceneNode[] = [];
  if (parent && isRotatingContainer(parent)) {
    containers.push(parent);
  }
  for (const ancestor of [...nodeAncestorChain(document, parentId)].reverse()) {
    if (isRotatingContainer(ancestor)) {
      containers.push(ancestor);
    }
  }
  return containers;
}

export function worldPointToParentLocalSpace(
  document: CurvaDocument,
  parentId: string,
  worldPoint: Point,
  childRotation: number,
  childBounds: Rect,
): Point {
  let point = worldPoint;
  const containers = rotatingContainersForChildOf(document, parentId);
  for (const container of [...containers].reverse()) {
    const rotation = nodeSelfRotation(container);
    if (rotation !== 0) {
      point = inverseRotatePoint(point, nodeRotationOrigin(container), rotation);
    }
  }

  if (childRotation !== 0) {
    const origin = rotationOriginForBounds(
      childBounds,
      childBounds,
      { x: 0, y: 0, rotation: childRotation },
    );
    point = inverseRotatePoint(point, origin, childRotation);
  }

  return point;
}

export function nodeLocalPlacementFromWorldGeometry(
  document: CurvaDocument,
  parentId: string,
  worldCorners: Point[],
  worldRotation: number,
): { bounds: Rect; rotation: number; rotationOrigin?: Point } {
  const containers = rotatingContainersForChildOf(document, parentId);
  const containerRotation = containers.reduce((sum, container) => sum + nodeSelfRotation(container), 0);
  const childRotation = normalizeDegrees(worldRotation - containerRotation);

  let localCorners = worldCorners.map(corner => ({ ...corner }));
  for (const container of [...containers].reverse()) {
    const rotation = nodeSelfRotation(container);
    if (rotation !== 0) {
      localCorners = localCorners.map(corner =>
        inverseRotatePoint(corner, nodeRotationOrigin(container), rotation),
      );
    }
  }

  let bounds: Rect;
  if (childRotation !== 0) {
    const provisionalBounds = boundsFromPoints(localCorners);
    const childOrigin = rotationOriginForBounds(
      provisionalBounds,
      provisionalBounds,
      { x: 0, y: 0, rotation: childRotation },
    );
    localCorners = localCorners.map(corner => inverseRotatePoint(corner, childOrigin, childRotation));
    bounds = boundsFromPoints(localCorners);
  } else {
    bounds = boundsFromPoints(localCorners);
  }

  const rotationOrigin = childRotation !== 0
    ? rotationOriginForBounds(bounds, bounds, { x: 0, y: 0, rotation: childRotation })
    : undefined;

  return { bounds, rotation: childRotation, rotationOrigin };
}

export function nodeLocalPlacementFromWorldBounds(
  document: CurvaDocument,
  parentId: string,
  worldBounds: Rect,
  worldRotation = 0,
): { bounds: Rect; rotation: number; rotationOrigin?: Point } {
  const worldCenter = rectCenter(worldBounds);
  const worldCorners = worldRotation === 0
    ? rectCorners(worldBounds)
    : rectCorners(worldBounds).map(corner => rotatePoint(corner, worldCenter, worldRotation));
  return nodeLocalPlacementFromWorldGeometry(document, parentId, worldCorners, worldRotation);
}

function remapNodeGeometryPoints(node: SceneNode, mapPoint: (point: Point) => Point): void {
  if (node.type === "vectorNetwork") {
    for (const vertex of node.network.vertices) {
      const next = mapPoint(vertex);
      vertex.x = next.x;
      vertex.y = next.y;
    }
    for (const edge of node.network.edges) {
      if (edge.from.handle) {
        edge.from.handle = mapPoint(edge.from.handle);
      }
      if (edge.to.handle) {
        edge.to.handle = mapPoint(edge.to.handle);
      }
    }
    node.bounds = getVectorNetworkBounds(node.network);
    return;
  }

  if (node.type === "shape") {
    const contours = pathDataToContours(node.path).map(contour => contour.map(mapPoint));
    node.path = contoursToPathData(contours);
    node.bounds = boundsFromPoints(contours.flat());
    return;
  }

  if (node.type === "boolean") {
    const contours = node.resolved.contours.map(contour => contour.map(mapPoint));
    node.resolved = {
      contours,
      path: contoursToPathData(contours),
    };
    node.bounds = boundsFromPoints(contours.flat());
  }
}

function applyPlacementTransform(
  transform: SceneNode["transform"],
  placement: { rotation: number; rotationOrigin?: Point },
): SceneNode["transform"] {
  if (placement.rotation === 0) {
    return { ...transform, rotation: 0, x: 0, y: 0 };
  }

  const origin = placement.rotationOrigin ?? { x: 0, y: 0 };
  return { ...transform, rotation: placement.rotation, x: origin.x, y: origin.y };
}

export function applyNodeLocalPlacementFromWorld(
  document: CurvaDocument,
  parentId: string,
  node: SceneNode,
  worldBounds: Rect,
  worldRotation = 0,
): SceneNode {
  const placement = nodeLocalPlacementFromWorldBounds(document, parentId, worldBounds, worldRotation);
  const updated = structuredClone(node);
  const worldToLocal = (point: Point) =>
    worldPointToParentLocalSpace(document, parentId, point, placement.rotation, placement.bounds);
  remapNodeGeometryPoints(updated, worldToLocal);
  // For line primitives, preserve the signed bounds (direction) by transforming the start and end
  // points individually instead of using boundsFromPoints which normalizes them, losing direction.
  if (updated.type === "primitive" && updated.primitive.kind === "line") {
    const localStart = worldToLocal({ x: worldBounds.x, y: worldBounds.y });
    const localEnd = worldToLocal({ x: worldBounds.x + worldBounds.width, y: worldBounds.y + worldBounds.height });
    updated.bounds = { x: localStart.x, y: localStart.y, width: localEnd.x - localStart.x, height: localEnd.y - localStart.y };
  } else {
    updated.bounds = placement.bounds;
  }
  updated.transform = applyPlacementTransform(updated.transform, placement);
  return updated;
}

export function reparentNodePreserveWorldAppearance(
  document: CurvaDocument,
  nodeId: string,
  newParentId: string,
): SceneNode {
  const node = findSceneNode(document, nodeId);
  if (!node) {
    throw new Error(`Node ${nodeId} was not found.`);
  }

  const worldCorners = nodeSelectionCorners(document, nodeId);
  if (!worldCorners) {
    throw new Error(`Node ${nodeId} has no placement geometry.`);
  }
  const worldRotation = nodeEffectiveWorldRotation(document, nodeId);
  const placement = nodeLocalPlacementFromWorldGeometry(document, newParentId, worldCorners, worldRotation);
  const updated = structuredClone(node);
  const toLocal = (localPoint: Point) => worldPointToParentLocalSpace(
    document,
    newParentId,
    nodeSelectionPointToWorld(document, nodeId, localPoint),
    placement.rotation,
    placement.bounds,
  );
  remapNodeGeometryPoints(updated, toLocal);
  updated.bounds = placement.bounds;
  updated.transform = applyPlacementTransform(updated.transform, placement);
  return updated;
}
